import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiMic } from 'react-icons/fi';
import {
  FAB_DEFAULT_OFFSET,
  FAB_DRAG_THRESHOLD_PX,
  FabOffset,
  clampFabOffset,
  loadFabOffset,
  saveFabOffset,
} from '../lib/fabPosition';

interface RegisterFabProps {
  onOpen: () => void;
}

/**
 * Botão flutuante "Registrar" que pode ser arrastado para fora da frente de outros botões.
 * Clique abre o Registrar; arrastar move e lembra a posição neste navegador.
 */
const RegisterFab: React.FC<RegisterFabProps> = ({ onOpen }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [offset, setOffset] = useState<FabOffset>(() => loadFabOffset() ?? FAB_DEFAULT_OFFSET);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ pointerId: number; x: number; y: number; start: FabOffset; moved: boolean } | null>(null);
  const suppressClick = useRef(false);

  const clampToViewport = useCallback((next: FabOffset): FabOffset => {
    const el = ref.current;
    const size = el ? { width: el.offsetWidth, height: el.offsetHeight } : { width: 56, height: 56 };
    return clampFabOffset(next, { width: window.innerWidth, height: window.innerHeight }, size);
  }, []);

  // Janela diminuiu: puxa o botão de volta para dentro da tela.
  useEffect(() => {
    const onResize = () => setOffset((prev) => clampToViewport(prev));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clampToViewport]);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    drag.current = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, start: offset, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved) {
      if (Math.hypot(dx, dy) < FAB_DRAG_THRESHOLD_PX) return;
      d.moved = true;
      setDragging(true);
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ponteiro já liberado */
      }
    }
    setOffset(clampToViewport({ right: d.start.right - dx, bottom: d.start.bottom - dy }));
  };

  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    if (!d.moved) return;
    // O click que o navegador dispara ao soltar não deve abrir o Registrar.
    suppressClick.current = true;
    window.setTimeout(() => { suppressClick.current = false; }, 0);
    setDragging(false);
    setOffset((prev) => {
      saveFabOffset(prev);
      return prev;
    });
  };

  const onClick = () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    onOpen();
  };

  return (
    <button
      ref={ref}
      type="button"
      className={`mai-fab${dragging ? ' is-dragging' : ''}`}
      style={{ right: offset.right, bottom: offset.bottom }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={onClick}
      title="Registrar o que aconteceu no mundo real. Arraste para mudar de lugar."
      aria-label="Registrar"
    >
      <span className="mai-fab-grip" aria-hidden="true" />
      <FiMic size={18} />
      <span className="mai-fab-label">Registrar</span>
    </button>
  );
};

export default RegisterFab;
